import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Snackbar,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import PhoneIcon from '@mui/icons-material/Phone';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SendIcon from '@mui/icons-material/Send';

const MotionBox = motion(Box);

const contactInfo = [
  {
    icon: <PhoneIcon />,
    title: 'Telephone Number',
    details: ['+966 13 834 2170'],
  },
  {
    icon: <SmartphoneIcon />,
    title: 'Mobile Numbers',
    details: ['+966 55 540 5414', '+966 58 343 0459'],
  },
  {
    icon: <EmailIcon />,
    title: 'Email Address',
    details: ['info@alamoudiest.com.sa'],
  },
  {
    icon: <LocationOnIcon />,
    title: 'Our Locations',
    details: ['Dammam, Saudi Arabia', 'Riyadh, Saudi Arabia'],
  },
  {
    icon: <AccessTimeIcon />,
    title: 'Working Hours',
    details: ['Sat - Thu: 8:00 AM - 8:00 PM', 'Friday: Closed'],
  },
];

const Contact = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Create WhatsApp message
    const message = `*New Inquiry from Website*%0A%0A*Name:* ${formData.name}%0A*Email:* ${formData.email}%0A*Phone:* ${formData.phone}%0A*Subject:* ${formData.subject}%0A*Message:* ${formData.message}`;
    window.open(`https://wa.me/966583430459?text=${message}`, '_blank');
    setSnackbar({ open: true, message: 'Redirecting to WhatsApp...', severity: 'success' });
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  return (
    <Box
      id="contact"
      sx={{
        py: { xs: 8, md: 15 },
        bgcolor: theme.palette.mode === 'dark' ? '#050505' : 'background.paper',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          backgroundImage: theme.palette.mode === 'dark' ? 'radial-gradient(circle at 10% 20%, rgba(229, 115, 115, 0.03) 0%, transparent 40%)' : 'none',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="overline"
            sx={{
              color: '#e57373',
              fontWeight: 700,
              letterSpacing: 3,
              fontFamily: '"Rajdhani", sans-serif',
              fontSize: '1rem',
            }}
          >
            Contact Support
          </Typography>
          <Typography
            variant="h2"
            sx={{
              mt: 1,
              mb: 2,
              fontFamily: '"Orbitron", sans-serif',
              fontWeight: 700,
              color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
              fontSize: { xs: '2rem', md: '3.5rem' },
            }}
          >
            GET IN
            <Box component="span" sx={{ color: '#e57373' }}>
              {' '}TOUCH
            </Box>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'text.secondary',
              maxWidth: 600,
              mx: 'auto',
              fontFamily: '"Rajdhani", sans-serif',
              fontSize: '1.2rem',
            }}
          >
            Have questions or need a quote? Reach out to us and our team will 
            get back to you as soon as possible.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Contact Info Cards */}
          <Grid item xs={12} lg={4}>
            <Grid container spacing={3}>
              {contactInfo.map((info, index) => (
                <Grid item xs={12} key={index}>
                  <MotionBox
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card
                      sx={{
                        borderRadius: 2,
                        bgcolor: theme.palette.mode === 'dark' ? '#111' : 'background.paper',
                        border: theme.palette.mode === 'dark' ? '1px solid #222' : '1px solid #eee',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: '#e57373',
                          transform: 'translateX(10px)',
                          boxShadow: '0 0 20px rgba(229, 115, 115, 0.1)',
                          bgcolor: theme.palette.mode === 'dark' ? '#151515' : 'white',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: 1,
                              bgcolor: 'rgba(229, 115, 115, 0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#e57373',
                              flexShrink: 0,
                              border: '1px solid rgba(229, 115, 115, 0.2)',
                            }}
                          >
                            {React.cloneElement(info.icon, { fontSize: 'large' })}
                          </Box>
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{ 
                                fontWeight: 700, 
                                mb: 0.5, 
                                fontSize: '1.2rem',
                                fontFamily: '"Rajdhani", sans-serif',
                                color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                                textTransform: 'uppercase',
                                letterSpacing: 1,
                              }}
                            >
                              {info.title}
                            </Typography>
                            {info.details.map((detail, i) => (
                              <Typography
                                key={i}
                                variant="body2"
                                sx={{ 
                                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'text.secondary', 
                                  mb: 0.5, 
                                  fontSize: '1rem',
                                  fontFamily: '"Rajdhani", sans-serif'
                                }}
                              >
                                {detail}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </MotionBox>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Contact Form */}
          <Grid item xs={12} lg={8}>
            <MotionBox
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card sx={{ 
                p: { xs: 3, md: 5 }, 
                borderRadius: 2,
                bgcolor: theme.palette.mode === 'dark' ? '#111' : 'background.paper',
                border: theme.palette.mode === 'dark' ? '1px solid #222' : '1px solid #eee',
                boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 10px 40px rgba(0,0,0,0.05)'
              }}>
                <CardContent>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, fontFamily: '"Orbitron", sans-serif', color: theme.palette.mode === 'dark' ? 'white' : 'text.primary' }}>
                    SEND US A MESSAGE
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 4, fontFamily: '"Rajdhani", sans-serif', fontSize: '1.1rem', color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'text.secondary' }}>
                    We'll get back to you within 24 hours.
                  </Typography>
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Your Name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          InputLabelProps={{ style: { color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : '#666', fontFamily: '"Rajdhani", sans-serif' } }}
                          InputProps={{ 
                            sx: { 
                              borderRadius: 1,
                              bgcolor: theme.palette.mode === 'dark' ? '#0a0a0a' : 'white',
                              color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                              fontFamily: '"Rajdhani", sans-serif',
                              '& fieldset': { borderColor: theme.palette.mode === 'dark' ? '#333' : '#ddd' },
                              '&:hover fieldset': { borderColor: theme.palette.mode === 'dark' ? '#555' : '#999' },
                              '&.Mui-focused fieldset': { borderColor: '#e57373' },
                            } 
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email Address"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          InputLabelProps={{ style: { color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : '#666', fontFamily: '"Rajdhani", sans-serif' } }}
                          InputProps={{ 
                            sx: { 
                              borderRadius: 1,
                              bgcolor: theme.palette.mode === 'dark' ? '#0a0a0a' : 'white',
                              color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                              fontFamily: '"Rajdhani", sans-serif',
                              '& fieldset': { borderColor: theme.palette.mode === 'dark' ? '#333' : '#ddd' },
                              '&:hover fieldset': { borderColor: theme.palette.mode === 'dark' ? '#555' : '#999' },
                              '&.Mui-focused fieldset': { borderColor: '#e57373' },
                            } 
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Phone Number"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          variant="outlined"
                          InputLabelProps={{ style: { color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : '#666', fontFamily: '"Rajdhani", sans-serif' } }}
                          InputProps={{ 
                            sx: { 
                              borderRadius: 1,
                              bgcolor: theme.palette.mode === 'dark' ? '#0a0a0a' : 'white',
                              color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                              fontFamily: '"Rajdhani", sans-serif',
                              '& fieldset': { borderColor: theme.palette.mode === 'dark' ? '#333' : '#ddd' },
                              '&:hover fieldset': { borderColor: theme.palette.mode === 'dark' ? '#555' : '#999' },
                              '&.Mui-focused fieldset': { borderColor: '#e57373' },
                            } 
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          InputLabelProps={{ style: { color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : '#666', fontFamily: '"Rajdhani", sans-serif' } }}
                          InputProps={{ 
                            sx: { 
                              borderRadius: 1,
                              bgcolor: theme.palette.mode === 'dark' ? '#0a0a0a' : 'white',
                              color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                              fontFamily: '"Rajdhani", sans-serif',
                              '& fieldset': { borderColor: theme.palette.mode === 'dark' ? '#333' : '#ddd' },
                              '&:hover fieldset': { borderColor: theme.palette.mode === 'dark' ? '#555' : '#999' },
                              '&.Mui-focused fieldset': { borderColor: '#e57373' },
                            } 
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Your Message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          multiline
                          rows={6}
                          variant="outlined"
                          InputLabelProps={{ style: { color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : '#666', fontFamily: '"Rajdhani", sans-serif' } }}
                          InputProps={{ 
                            sx: { 
                              borderRadius: 1,
                              bgcolor: theme.palette.mode === 'dark' ? '#0a0a0a' : 'white',
                              color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                              fontFamily: '"Rajdhani", sans-serif',
                              '& fieldset': { borderColor: theme.palette.mode === 'dark' ? '#333' : '#ddd' },
                              '&:hover fieldset': { borderColor: theme.palette.mode === 'dark' ? '#555' : '#999' },
                              '&.Mui-focused fieldset': { borderColor: '#e57373' },
                            } 
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          endIcon={<SendIcon />}
                          sx={{
                            px: 6,
                            py: 2,
                            bgcolor: '#e57373',
                            borderRadius: 0,
                            fontSize: '1rem',
                            fontWeight: 700,
                            fontFamily: '"Orbitron", sans-serif',
                            letterSpacing: 2,
                            '&:hover': {
                              bgcolor: theme.palette.mode === 'dark' ? 'white' : '#d32f2f',
                              color: theme.palette.mode === 'dark' ? '#e57373' : 'white',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          Send Message
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </CardContent>
              </Card>
            </MotionBox>
          </Grid>
        </Grid>

        {/* Map Section */}
        <Grid container spacing={3} sx={{ mt: 6 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Dammam Branch
            </Typography>
            <Box sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 3 }}>
              <iframe
                title="Al Amoudi Bearing - Dammam"
                src="https://www.google.com/maps?q=MOHAMMED+SAEED+AL+AMOUDI+TRADING+EST,+8172+King+Khaled+St,+Al+Adamah,+Dammam+32242&ftid=0x3e49fb8cfb749891:0xed75bb7697c08030&output=embed"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Riyadh Branch
            </Typography>
            <Box sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 3 }}>
              <iframe
                title="Al Amoudi Bearing - Riyadh"
                src="https://www.google.com/maps?q=MOHAMMED+SAEED+AL+AMOUDI,+Al+Kharj+Rd,+As+Salhiyah,+Riyadh+12662&ftid=0x3e2f05c36981f98d:0xc724cea57c55e76e&output=embed"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;
